import { FormControl, FormHelperText, FormLabel, IconButton, Input, makeStyles, Theme, Typography, Box, LinearProgress } from '@material-ui/core';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import React, { useState, useRef, useEffect } from 'react';
import api, { ApiError, isApiError } from '../api/api';
import { useUser } from '../contexts/UserContext';
import { FileUploadUrl } from '../types/types';
import Button from './Button';

const useStyles = makeStyles<Theme>(() => ({
  root: {
    minWidth: 150,
    margin: '10px 0',
    // left icon
    '& .MuiInput-underline:before': {
      content: '',
      display: 'block',
      height: 30,
      width: 30,
      margin: 5,
      borderBottom: 'none'
    },
    '& .MuiInput-underline:hover:before': {
      borderBottom: 'none'
    },
    // right icon
    '& .MuiInput-underline:after': {
      display: 'block',
      transform: 'none',
      transition: 'none',
      height: 24,
      width: 24,
      margin: 8,
      left: 'auto',
      right: 0,
      top: 0,
      borderBottom: 'none'
    },
    // input
    '& .MuiInput-input': {
      fontSize: 16,
      color: '#212632',
      height: 30,
      padding: 5,
      border: 'none',
      borderRadius: 4,
      background: '#EFF0FF'
    },
    // input label
    '& .MuiFormLabel-root': {
      color: '#5C657C',
      fontWeight: 500,
      fontSize: 14
    },
    '& label + .MuiInput-formControl': {
      marginTop: 0
    },
    // remove native file icon
    '& .MuiInput-input::-webkit-file-upload-button': {
      visibility: 'hidden'
    },
    '$ .MuiInput-input, input[type="file"]': {
      display: 'none'
    },
    // helper text
    '& .MuiFormHelperText-root': {
      fontWeight: 300,
      fontSize: 12
    }
  }
}));

const useGeneralStyles = makeStyles(() => ({
  file: {
    width: 270,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    borderRadius: 4,
    backgroundColor: '#EFF0FF',
    '& > p': {
      flexGrow: 1
    }
  }
}));
interface FileUploaderProps {
  label: string
  id: string
  actionLabel: string
  fileUrl: string
  filePath: string
  fileTypes: string
  autoFocus?: boolean
  callback: (url: string | null) => void
}

const FileUploader: React.FC<FileUploaderProps> = ({
  label, id, actionLabel, fileUrl, filePath, fileTypes, autoFocus = false, callback
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [autoOpen, setAutoOpen] = useState(autoFocus);
  const { getAccessToken } = useUser();

  const handleGetUploadUrl = async (file: File, fileName: string, fileExt: string) => {
    try {
      setUploading(true);
      const accessToken = await getAccessToken();
      const uploadUrl = await api.getUploadFileUrl(accessToken, fileName, fileExt);
      await fetch((uploadUrl as FileUploadUrl).postUrl, { // Upload file to S3 using presigned URL.
        method: 'PUT',
        headers: {
          'Content-Type': ''
        },
        body: file
      });
      callback(uploadUrl.postUrl);
    } catch (e: unknown) {
      console.error(e);
      if (isApiError(e)) {
        setError((e as ApiError).message);
      } else {
        setError('An unexpected error occurred uploading the file.');
      }
    } finally {
      setUploading(false);
    }
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target as HTMLInputElement;
    const file = files && files[0];
    setError(null);
    if (file) {
      // check if the file type is allowed
      const extRegex = new RegExp('(.*)[.](ppt|pptx|pdf|doc|docx)$');
      const matched = file.name?.match(extRegex);
      if (file && matched && matched.length > 2) {
        setUploadFile(file);
        const fileName = matched[1];
        const fileExt = matched[2];
        handleGetUploadUrl(file, filePath + fileName, fileExt);
      } else {
        setError('File type not supported');
      }
    }
  };

  useEffect(() => {
    const input = inputRef?.current;
    if (input && autoFocus) {
      input.click();
    }
  }, [inputRef, autoFocus]);

  useEffect(() => {
    const currentInputRef = inputRef?.current as HTMLInputElement;
    if (uploadFile) {
      return () => {
        // clean files from input to avoid any memory leak
        currentInputRef.files = null;
      };
    } else {
      return () => {};
    }
  }, [uploadFile]);

  useEffect(() => {
    if (fileUrl) {
      // find the file name out of url such as:
      // https://s3.amazonaws.com/ventureraise/pitchDeck.pdf => pitchDeck.pdf
      const regMatches = fileUrl.match(/(?:.*\/)(.*)/);
      if (regMatches && regMatches.length > 1) {
        const file = new File([], regMatches[1]);
        setUploadFile(file);
      } else {
        const file = new File([], fileUrl);
        setUploadFile(file);
      }
    }
  }, [fileUrl]);

  useEffect(() => {
    let timer: any;
    if (isUploading) {
      timer = setInterval(() => {
        setProgress((oldProgress) => {
          if (oldProgress === 100) {
            return 0;
          }
          const diff = Math.random() * 10;
          return Math.min(oldProgress + diff, 100);
        });
      }, 500);
    } else if (timer) {
      clearInterval(timer);
    }

    return () => {
      clearInterval(timer);
    };
  }, [isUploading]);

  const classes = useStyles();
  const styles = useGeneralStyles();
  const ButtonComponent = () => (
    <>
      <Button
        variant='text'
        color='secondary'
        size='large'
        startIcon={<AddRoundedIcon />}
        onClick={() => {
          console.log(inputRef.current);
          inputRef.current?.click();
        }}
      >
        {actionLabel}
      </Button>
      <FormHelperText error={!!error}>{error || `File types: ${fileTypes}`}</FormHelperText>
    </>
  );

  const ProgressComponent = () => (
    <Box margin='10px 0'>
      <Typography>Loading....</Typography>
      <LinearProgress variant='determinate' value={progress} />
    </Box>
  );

  const LoadingComponent = isUploading ? ProgressComponent : ButtonComponent;

  return (
    <FormControl classes={classes}>
      <FormLabel id='pitch-deck' htmlFor='pitch-deck'>{label}</FormLabel>
      <Input onChange={onChange} style={{ display: 'none' }} type='file' inputRef={inputRef} id={id} name={id} />
      {
        !isUploading && uploadFile ? (
          <Box className={styles.file}>
            <InsertDriveFileOutlinedIcon color='primary' style={{ margin: '0 8px 0 5px' }} />
            <Typography noWrap variant='body1'>{(uploadFile as File).name}</Typography>
            <IconButton
              style={{ padding: 8 }}
              onClick={() => {
                (inputRef.current as HTMLInputElement).files = null;
                setUploadFile(null);
                callback(null);
              }}
            >
              <ClearRoundedIcon />
            </IconButton>
          </Box>
        ) : <LoadingComponent />
       }
    </FormControl>
  );
};

export default FileUploader;

